import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import APIError from 'types/api_error';
import Pipeline, { PipelineDefinition } from 'types/pipeline';

import {
  updateQueriesData,
  UpdateQueriesDataContext,
} from 'services/update_queries_data';
import { useAPI } from 'hooks/api/useAPI';

import { initializeAPIPipeline } from './usePipelines';

type UseUpdatePipelineParams =
  | { id: string; name: string }
  | { id: string; definition: PipelineDefinition };

export function useUpdatePipeline({
  onMutate,
  onError,
  onSuccess,
  onSettled,
  ...options
}: UseMutationOptions<
  Pipeline,
  APIError,
  UseUpdatePipelineParams,
  UpdateQueriesDataContext<Pipeline>
> = {}) {
  const { applicationID, client } = useAPI();
  const queryClient = useQueryClient();

  return useMutation<
    Pipeline,
    APIError,
    UseUpdatePipelineParams,
    UpdateQueriesDataContext<Pipeline>
  >(
    async ({ id, ...params }) => {
      const { data: pipeline } = await client.put<Pipeline>(
        `/apps/${applicationID}/pipelines/${id}`,
        {
          ...params,
        }
      );

      return initializeAPIPipeline(
        new Pipeline(
          pipeline.id,
          pipeline.name,
          pipeline.application_id,
          pipeline.definition,
          pipeline.created_at,
          pipeline.updated_at
        )
      );
    },
    {
      ...options,
      async onMutate({ id, ...params }) {
        await queryClient.cancelQueries(['pipelines']);
        await queryClient.cancelQueries(['pipeline']);

        onMutate?.({ id, ...params });

        return updateQueriesData<Pipeline>({
          queryClient,
          listQueryKey: ['pipelines'],
          singleQueryKey: ['pipeline'],
          ids: [id],
          updateData(pipeline) {
            const updatedPipeline = pipeline.copy();

            if ('name' in params) {
              updatedPipeline.name = params.name;
            }

            if ('definition' in params) {
              updatedPipeline.definition = params.definition;
            }

            return updatedPipeline;
          },
        });
      },
      onError(error, params, context) {
        onError?.(error, params, context);

        context?.previousLists?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
        context?.previousSingles?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
      },
      onSettled(...args) {
        onSettled?.(...args);
        queryClient.invalidateQueries(['pipeline']);
        queryClient.invalidateQueries(['pipelines']);
      },
    }
  );
}
